import { isEmpty } from "lodash-es"
import { useFormikContext } from "formik"
import { MenuItem, Typography } from "@mui/material"
import { IdCardSignupFormValues, IdCardSubscriptionUser, TransformedCompany } from "../../loginTypes"
import { FormikDropdown, FormikTextField } from "../../../../shared/components/forms/Formik"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { MAX_NAME_LENGTH } from "../../../../constants/inputValidationDefaults"

type IdCardSignupFormContentCompanyProps = Readonly<{
    companiesList: TransformedCompany[]
    isUserExists: boolean
    existingUser?: IdCardSubscriptionUser
}>

export const IdCardSignupFormContentCompany = ({
    companiesList,
    isUserExists,
    existingUser,
}: IdCardSignupFormContentCompanyProps) => {
    const { values, setFieldValue } = useFormikContext<IdCardSignupFormValues>()

    const selectedCompany = companiesList.find((company) => company.id === values.companyId)

    const handleCompanyChange = (selectedId: string) => {
        const newCompany = companiesList.find((company) => company.id === selectedId) || null
        const newUserGroupId = newCompany
            ? newCompany.isGroup
                ? newCompany.userGroupId
                : newCompany?.userGroups?.length === 1
                ? newCompany.userGroups[0].id
                : null
            : null

        setFieldValue("companyId", newCompany?.id)
        setFieldValue("userGroupId", newUserGroupId)
        setFieldValue("displayName", "")
        setFieldValue("mobileNumber", "")
    }

    return (
        <>
            {companiesList.length > 1 ? (
                <LocalizedStrict id="id-card-signup-field-company" attrs={{ label: true, placeholder: true }}>
                    <FormikDropdown<IdCardSignupFormValues>
                        fullWidth
                        name="companyId"
                        label="Your company:"
                        placeholder="Select the company you work for"
                        onChange={(e) => handleCompanyChange(e.target.value as string)}
                    >
                        {companiesList.map((company) => (
                            <MenuItem key={company.id} value={company.id}>
                                {company.displayName}
                            </MenuItem>
                        ))}
                    </FormikDropdown>
                </LocalizedStrict>
            ) : (
                companiesList.length === 1 && (
                    <Typography>
                        <LocalizedStrict id="id-card-signup-using-company">
                            <>Your company:</>
                        </LocalizedStrict>{" "}
                        <Typography component="span" fontWeight="bold">
                            {companiesList[0].displayName}
                        </Typography>
                    </Typography>
                )
            )}

            {selectedCompany && !selectedCompany?.isGroup && selectedCompany.userGroups.length > 1 ? (
                <LocalizedStrict id="id-card-signup-field-user-group" attrs={{ label: true, placeholder: true }}>
                    <FormikDropdown<IdCardSignupFormValues>
                        fullWidth
                        name="userGroupId"
                        label="Your user group:"
                        placeholder="Select user group"
                    >
                        {!selectedCompany?.isGroup &&
                            selectedCompany?.userGroups.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.name}
                                </MenuItem>
                            ))}
                    </FormikDropdown>
                </LocalizedStrict>
            ) : (
                selectedCompany &&
                !selectedCompany.isGroup &&
                selectedCompany.userGroups?.length === 1 && (
                    <Typography>
                        <LocalizedStrict id="id-card-signup-using-user-group">
                            <>Your user group:</>
                        </LocalizedStrict>{" "}
                        <Typography component="span" fontWeight="bold">
                            {" "}
                            {selectedCompany.userGroups[0].name}
                        </Typography>
                    </Typography>
                )
            )}

            {isUserExists && !isEmpty(existingUser) && (
                <Typography>
                    <LocalizedStrict id="id-card-signup-using-user-name">
                        <>User name:</>
                    </LocalizedStrict>{" "}
                    <Typography component="span" fontWeight="bold">
                        {existingUser?.displayName}
                    </Typography>
                </Typography>
            )}

            {values.companyId && values.userGroupId && !isUserExists && (
                <>
                    <LocalizedStrict id="id-card-signup-field-name" attrs={{ label: true, placeholder: true }}>
                        <FormikTextField<IdCardSignupFormValues>
                            name="displayName"
                            label="Your name (*):"
                            placeholder="Enter your full name"
                            fullWidth
                            inputProps={{ maxLength: MAX_NAME_LENGTH }}
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="id-card-signup-field-mobile-number" attrs={{ label: true, placeholder: true }}>
                        <FormikTextField<IdCardSignupFormValues>
                            name="mobileNumber"
                            label="Your phone:"
                            placeholder="Enter your mobile number (optional)"
                            autoComplete="mobile"
                            fullWidth
                        />
                    </LocalizedStrict>
                </>
            )}
        </>
    )
}
